import { AxiosResponse } from 'axios';
import api from './axiosConfig';
import { invalidando } from './queryClient';
import { StepLog } from '../types/entities';
import { StepReaction } from '../types/enums';

interface StepReactionPayload {
  routineStepId: string;
  stepName: string;
  reaction: StepReaction | null;
  notes?: string;
}

interface StepReactionStats {
  stepName: string;
  great: number;
  neutral: number;
  irritated: number;
}

/** Etapa que irritou a pele 3+ vezes seguidas (regra no backend). */
export interface StepIrritationAlert {
  stepName: string;
  consecutiveIrritations: number;
}

export const CHAVES_REACOES = {
  dominio: ['reacoes-etapa'] as const,
  stats: (userId: string) => ['reacoes-etapa', 'stats', userId] as const,
  alertas: (userId: string) => ['reacoes-etapa', 'alertas', userId] as const,
  porLog: (userId: string, logId: string) => ['reacoes-etapa', 'log', userId, logId] as const,
};

const StepReactionService = {
  /**
   * GET /api/v1/users/{userId}/logs/{logId}/step-reactions
   * Retorna as reações de um registro específico.
   */
  getByLog: (userId: string, logId: string): Promise<AxiosResponse<StepLog[]>> =>
    api.get(`/users/${userId}/logs/${logId}/step-reactions`),

  /**
   * POST /api/v1/users/{userId}/logs/{logId}/step-reactions/batch
   * Cria múltiplas reações de uma vez.
   */
  createBatch: (userId: string, logId: string, reactions: StepReactionPayload[]): Promise<AxiosResponse<StepLog[]>> =>
    api.post(`/users/${userId}/logs/${logId}/step-reactions/batch`, reactions).then(invalidando(CHAVES_REACOES.dominio)),

  /**
   * PUT /api/v1/users/{userId}/logs/{logId}/step-reactions/{stepLogId}
   */
  update: (userId: string, logId: string, stepLogId: string, data: Partial<StepReactionPayload>): Promise<AxiosResponse<StepLog>> =>
    api.put(`/users/${userId}/logs/${logId}/step-reactions/${stepLogId}`, data).then(invalidando(CHAVES_REACOES.dominio)),

  /**
   * DELETE /api/v1/users/{userId}/logs/{logId}/step-reactions/{stepLogId}
   */
  remove: (userId: string, logId: string, stepLogId: string): Promise<AxiosResponse<void>> =>
    api.delete(`/users/${userId}/logs/${logId}/step-reactions/${stepLogId}`).then(invalidando(CHAVES_REACOES.dominio)),

  /**
   * GET /api/v1/users/{userId}/step-reaction-stats
   * Retorna estatísticas de compatibilidade por etapa.
   */
  getStats: (userId: string): Promise<AxiosResponse<StepReactionStats[]>> =>
    api.get(`/users/${userId}/step-reaction-stats`),

  /**
   * GET /api/v1/users/{userId}/step-irritation-alerts
   * Etapas que irritaram a pele 3+ vezes seguidas — sugestão de remover/trocar.
   */
  getIrritationAlerts: (userId: string): Promise<AxiosResponse<StepIrritationAlert[]>> =>
    api.get(`/users/${userId}/step-irritation-alerts`),
};

export default StepReactionService;
